import type { ChangeEvent, ReactNode, RefObject } from "react";
import { useState } from "react";
import { SignatureModal } from "../../../components/Form/SignatureModel";
import { DatalistInput } from "../components/dataListInput";
import { useSettings } from "../../../context/SettingContext";
import { FormDatePicker } from "../../../components/Form/FormDatePicker";
import type { LegalAdviceFormThreeData } from "../../../types/LegalAdvices/LegaladviceFormThree.type";

export interface AttachmentFile {
  name: string;
  [key: string]: any;
}

export interface DetailFormDocumentProps {
  formData: LegalAdviceFormThreeData;
  isEditable: boolean;
  isSigning: boolean;
  sigCanvasRef: RefObject<any>;
  setIsSigning: (isSigning: boolean) => void;

  handleChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSaveSignature: (role?: 'prosecutor' | 'USER') => void;
  renderDocumentRow?: (label: string, fieldName: keyof LegalAdviceFormThreeData) => ReactNode;
  renderDatePicker?: (fieldName: keyof LegalAdviceFormThreeData) => ReactNode;

  // Action controls
  isPreview?: boolean;
  handleBackNavigation: () => void;
  handleClearDate?: (fieldName: keyof LegalAdviceFormThreeData) => void;
}

export const LegalDetailFormThreeDocument: React.FC<DetailFormDocumentProps> = ({
  formData,
  isEditable,
  isSigning,
  sigCanvasRef,
  setIsSigning,
  handleChange,
  handleSaveSignature,
  handleClearDate,
}) => {
  const { settings } = useSettings();

  // Track active signature role: 'prosecutor' or 'USER' (Responsible Officer)
  const [activeSigningRole, setActiveSigningRole] = useState<'prosecutor' | 'USER' | null>(null);

  // Check if either signature block is fully signed

  const OFFICE_OPTIONS = settings?.offices.map((o) => o.name) || [];

  const handleOpenSignatureModal = (role: 'prosecutor' | 'USER') => {
    if (!isEditable) return;
    setActiveSigningRole(role);
    setIsSigning(true);
  };

  const handleModalSave = () => {
    if (activeSigningRole) {
      handleSaveSignature(activeSigningRole);
    } else {
      handleSaveSignature();
    }
    setIsSigning(false);
    setActiveSigningRole(null);
  };

  return (
    /* A4 Sheet Container */
    <div className="w-full max-w-[800px] mx-auto bg-white shadow-lg border border-gray-300 p-12 text-black leading-relaxed relative text-sm md:text-base">
      <div className="absolute top-4 right-4 text-xs font-bold">ဥပရအမှုလိုက်ပုံစံ (၃)</div>

      {/* Header Title Section */}
      <div className="text-center space-y-1 mt-6">
        <h1 className="text-base md:text-lg font-bold">ပြည်ထောင်စုသမ္မတမြန်မာနိုင်ငံတော်အစိုးရ</h1>
        <h2 className="text-sm md:text-base font-bold">ဥပဒေရေးရာဝန်ကြီးဌာန</h2>
        <div className="flex justify-center items-center gap-2">
          <DatalistInput
            name="officerOffice"
            value={formData.officerOffice || ''}
            onChange={handleChange}
            placeholder=""
            options={OFFICE_OPTIONS}
            disabled={!isEditable}
          />
          <span className="w-24 text-left">ရုံး</span>
        </div>
        <div className="pt-4 flex flex-col items-center">
          <div className="border border-black px-6 py-3 inline-block bg-transparent space-y-2">
            <div className="flex items-center justify-center gap-1 font-bold flex-wrap">
              <span>ဥပရမှတ်ပုံတင် (</span>
              <input
                type="text"
                name="regNo"
                value={formData.regNo || ''}
                onChange={handleChange}
                disabled={!isEditable}
                placeholder=""
                className="w-16 text-center bg-transparent border-b border-dotted border-black focus:outline-none disabled:bg-transparent"
              />
              <span>) အမှတ်စဉ်</span>
              <input
                type="text"
                name="regSerialNumber"
                value={formData.regSerialNumber || ''}
                onChange={handleChange}
                disabled={!isEditable}
                placeholder=""
                className="w-24 text-center bg-transparent border-b border-dotted border-black focus:outline-none disabled:bg-transparent"
              />
            </div>
            <p className="font-bold text-base text-center">ပြစ်မှုဆိုင်ရာမူလမှု ပြီးပြတ်ချက်ပုံစံ</p>
          </div>
        </div>
      </div>

      {/* Document Form Fields */}
      <div className="mt-8 space-y-5 text-justify">
        {/* ၁။ တရားရုံး */}
        <div className="flex items-center gap-2 flex-wrap md:flex-nowrap">
          <span className="font-bold whitespace-nowrap">၁။ တရားရုံးအမည်</span>
          <DatalistInput
            name="courtName"
            value={formData.courtName || ''}
            onChange={handleChange}
            placeholder="တရားရုံး ရွေးချယ်ရန်"
            options={OFFICE_OPTIONS}
            disabled={!isEditable}
            className="flex-1 bg-transparent border-b border-black focus:outline-none py-0.5 text-center min-w-[200px]"
          />
        </div>

        {/* ၂။ အမှုအမှတ် */}
        <div className="flex items-center gap-2">
          <span className="font-bold whitespace-nowrap">၂။ အမှုအမှတ် -</span>
          <input
            type="text"
            name="caseNumber"
            value={formData.caseNumber || ''}
            onChange={handleChange}
            disabled={!isEditable}
            className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2 disabled:bg-transparent"
          />
        </div>

        {/* ၃။ တရားလိုအမည် */}
        <div className="flex items-center gap-2">
          <span className="font-bold whitespace-nowrap">၃။ တရားလိုအမည် -</span>
          <input
            type="text"
            name="plaintiffName"
            value={formData.plaintiffName || ''}
            onChange={handleChange}
            disabled={!isEditable}
            className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2 disabled:bg-transparent"
          />
        </div>

        {/* ၄။ တရားခံအမည်နှင့် နိုင်ငံသားစိစစ်ရေးကတ်ပြားအမှတ် */}
        <div className="flex items-start gap-2">
          <span className="font-bold whitespace-nowrap">၄။ တရားခံအမည်နှင့် နိုင်ငံသားစိစစ်ရေးကတ်ပြားအမှတ်/နိုင်ငံခြားသားမှတ်ပုံတင်အမှတ် -</span>
          <input
            type="text"
            name="defendantNameAndNrc"
            value={formData.defendantNameAndNrc || ''}
            onChange={handleChange}
            disabled={!isEditable}
            className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2 disabled:bg-transparent"
          />
        </div>

        {/* ၅။ တရားခံ၏ အဘအမည် */}
        <div className="flex items-center gap-2">
          <span className="font-bold whitespace-nowrap">၅။ တရားခံ၏ အဘအမည် -</span>
          <input
            type="text"
            name="defendantFatherName"
            value={formData.defendantFatherName || ''}
            onChange={handleChange}
            disabled={!isEditable}
            className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2 disabled:bg-transparent"
          />
        </div>

        {/* ၆။ တရားခံ၏နေရပ်လိပ်စာ */}
        <div className="flex items-center gap-2">
          <span className="font-bold whitespace-nowrap">၆။ တရားခံ၏နေရပ်လိပ်စာ -</span>
          <input
            type="text"
            name="defendantAddress"
            value={formData.defendantAddress || ''}
            onChange={handleChange}
            disabled={!isEditable}
            className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2 disabled:bg-transparent"
          />
        </div>

        {/* ၇။ ရဲစခန်းအမည် ပထမသတင်းပေးချက်အမှတ်နှင့်ရက်စွဲ */}
        <div className="flex items-start gap-2">
          <span className="font-bold whitespace-nowrap">၇။ ရဲစခန်း အမည် ပထမ သတင်းပေးချက်အမှတ်နှင့် ရက်စွဲ -</span>
          <input
            type="text"
            name="policeStationAndFirstReportNo"
            value={formData.policeStationAndFirstReportNo || ''}
            onChange={handleChange}
            disabled={!isEditable}
            className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2 disabled:bg-transparent"
          />
        </div>

        {/* ၈။ အမှုဖြစ်စဉ်အကျဉ်း */}
        <div className="space-y-1">
          <span className="font-bold block">၈။ အမှုဖြစ်စဉ်အကျဉ်း -</span>
          <textarea
            name="briefFacts"
            value={formData.briefFacts || ''}
            onChange={handleChange}
            disabled={!isEditable}
            rows={4}
            className="w-full p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none text-sm disabled:bg-gray-50"
          />
        </div>

        {/* ၉။ တရားစွဲဆိုတင်ပို့သည့် ဥပဒေပုဒ်မနှင့် ရက်စွဲ */}
        <div className="flex items-center gap-2">
          <span className="font-bold whitespace-nowrap">၉။ တရားစွဲဆိုတင်ပို့သည့် ဥပဒေပုဒ်မနှင့် ရက်စွဲ -</span>
          <input
            type="text"
            name="chargeSectionAndDate"
            value={formData.chargeSectionAndDate || ''}
            onChange={handleChange}
            disabled={!isEditable}
            className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2 disabled:bg-transparent"
          />
        </div>

        {/* ၁၀။ စွဲချက်တင်မိန့်လွှတ်သည့် စီရင်ချက် */}
        <div className="space-y-1">
          <span className="font-bold block">၁၀။ စွဲချက်မတင်မီလွှတ်သည့် စီရင်ချက်၊ အမိန့်အကျဉ်းနှင့်ရက်စွဲ (တရားရုံးကလွှတ်သည့် အဓိက အကြောင်းပြချက် ဖော်ပြရန်)</span>
          <textarea
            name="judgmentSentenceAndDate"
            value={formData.judgmentSentenceAndDate || ''}
            onChange={handleChange}
            disabled={!isEditable}
            rows={2}
            className="w-full p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none text-sm disabled:bg-gray-50"
          />
        </div>

        {/* ၁၁။ တရားရုံးက စွဲချက်တင်သည့် ဥပဒေ ပုဒ်မနှင့် ရက်စွဲ */}
        <div className="flex items-center gap-2">
          <span className="font-bold whitespace-nowrap">၁၁။ တရားရုံးက စွဲချက်တင်သည့် ဥပဒေ ပုဒ်မနှင့် ရက်စွဲ -</span>
          <input
            type="text"
            name="appealedSectionAndDate"
            value={formData.appealedSectionAndDate || ''}
            onChange={handleChange}
            disabled={!isEditable}
            className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2 disabled:bg-transparent"
          />
        </div>

        {/* ၁၂။ တရားရုံး၏ အပြီးသတ်စီရင်ချက် */}
        <div className="flex items-center gap-2">
          <span className="font-bold whitespace-nowrap">၁၂။ တရားရုံး၏ အပြီးသတ်စီရင်ချက်/အမိန့်အကျဉ်းနှင့် ရက်စွဲ -</span>
          <input
            type="text"
            name="appellateDecisionAndDate"
            value={formData.appellateDecisionAndDate || ''}
            onChange={handleChange}
            disabled={!isEditable}
            className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none px-2 disabled:bg-transparent"
          />
        </div>

        {/* ၁၃။ ပြစ်ဒဏ်ပေးသည့်ဥပဒေ ပုဒ်မ */}
        <div className="space-y-1">
          <span className="font-bold block">၁၃။ ပြစ်ဒဏ်ပေးသည့်ဥပဒေ ပုဒ်မ (အပြီးအပြတ်လွှတ်ပါကလွှတ်သည့် အဓိက အကြောင်းပြချက် ဖော်ပြရန်) -</span>
          <textarea
            name="acquittalGrounds"
            value={formData.acquittalGrounds || ''}
            onChange={handleChange}
            disabled={!isEditable}
            rows={2}
            className="w-full p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none text-sm disabled:bg-gray-50"
          />
        </div>

        {/* ၁၄။ အမှုလိုက် ဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက် အကျဉ်းချုပ် */}
        <div className="space-y-3 pt-2">
          <span className="font-bold block">၁၄။ အမှုလိုက် ဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက် အကျဉ်း --</span>

          <div className="pl-6 space-y-3">
            <div className="flex items-center gap-2">
              <span>(က) စီရင်ချက်/အမိန့်မှန်ကန်မှု</span>
              <input
                type="text"
                name="judgmentCorrectness"
                placeholder="ရှိ / မရှိ"
                value={formData.judgmentCorrectness || ''}
                onChange={handleChange}
                disabled={!isEditable}
                className="w-32 bg-transparent border-b border-dotted border-black focus:outline-none text-center disabled:bg-transparent"
              />
            </div>

            <div className="flex items-center gap-2">
              <span>(ခ) ပြင်ဆင်မှု သို့မဟုတ် အယူခံမှုတက်ရောက်ရန်လိုအပ်ခြင်း</span>
              <input
                type="text"
                name="revisionRequired"
                placeholder="ရှိ / မရှိ"
                value={formData.revisionRequired || ''}
                onChange={handleChange}
                disabled={!isEditable}
                className="w-32 bg-transparent border-b border-dotted border-black focus:outline-none text-center disabled:bg-transparent"
              />
            </div>

            <div className="space-y-1">
              <span>(ဂ) အကျိုးအကြောင်း ဖော်ပြချက် -</span>
              <textarea
                name="reasons"
                value={formData.reasons || ''}
                onChange={handleChange}
                disabled={!isEditable}
                rows={2}
                className="w-full p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none text-sm disabled:bg-gray-50"
              />
            </div>
          </div>

          {/* Section 14 Signature Box (Prosecutor) */}
          <div className="mt-6 flex flex-col items-end pr-6">
            <div className="w-64 flex flex-col space-y-2">
              <div className="flex items-center">
                <span className="w-20 text-right text-sm mr-2">လက်မှတ်</span>
                <div
                  onClick={() => handleOpenSignatureModal('prosecutor')}
                  className={`flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center transition relative group ${
                    isEditable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'
                  }`}
                >
                  {formData.prosecutorSignatureImage ? (
                    <img
                      src={formData.prosecutorSignatureImage}
                      alt="Prosecutor Signature"
                      className="h-8 object-contain mix-blend-multiply"
                    />
                  ) : (
                    <span className="text-gray-400 text-xs">
                      {isEditable ? '✍️ နှိပ်၍လက်မှတ်ထိုးပါ' : ''}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <span className="w-20 text-right text-sm mr-2">အမည်</span>
                <input
                  type="text"
                  name="prosecutorName"
                  value={formData.prosecutorName || ''}
                  onChange={handleChange}
                  disabled={!isEditable}
                  className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center disabled:bg-transparent"
                />
              </div>
              <p className="text-sm font-bold text-center pt-1">အမှုလိုက်ဥပဒေအရာရှိ</p>

              <div className="flex items-center gap-3">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">ရက်စွဲ</span>
                <div className="min-w-0 flex-1">
                  <FormDatePicker
                    name="prosecutorSubmissionDate"
                    value={formData.prosecutorSubmissionDate || ""}
                    isEditable={isEditable}
                    onChange={handleChange}
                    onClear={handleClearDate}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ၁၅။ တာဝန်ခံဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက် */}
        <div className="space-y-3 pt-4 border-t border-gray-200">
          <span className="font-bold block">၁၅။ တာဝန်ခံဥပဒေအရာရှိ၏ သဘောထားမှတ်ချက် -</span>
          <textarea
            name="responsibleOfficerOpinion"
            value={formData.responsibleOfficerOpinion || ''}
            onChange={handleChange}
            disabled={!isEditable}
            rows={3}
            className="w-full p-2 bg-transparent border border-gray-300 focus:outline-none focus:border-black resize-none text-sm disabled:bg-gray-50"
          />

          {/* Section 15 Signature Box (Responsible Officer) */}
          <div className="mt-6 flex flex-col items-end pr-6">
            <div className="w-64 flex flex-col space-y-2">
              <div className="flex items-center">
                <span className="w-20 text-right text-sm mr-2">လက်မှတ်</span>
                <div
                  onClick={() => handleOpenSignatureModal('USER')}
                  className={`flex-1 bg-transparent border-b border-dotted border-black min-h-[32px] flex items-center justify-center transition relative group ${
                    isEditable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'
                  }`}
                >
                  {formData.officerSignatureImage ? (
                    <img
                      src={formData.officerSignatureImage}
                      alt="Officer Signature"
                      className="h-8 object-contain mix-blend-multiply"
                    />
                  ) : (
                    <span className="text-gray-400 text-xs">
                      {isEditable ? '✍️ နှိပ်၍လက်မှတ်ထိုးပါ' : ''}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <span className="w-20 text-right text-sm mr-2">အမည်</span>
                <input
                  type="text"
                  name="officerName"
                  value={formData.officerName || ''}
                  onChange={handleChange}
                  disabled={!isEditable}
                  className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center disabled:bg-transparent"
                />
              </div>
              <p className="text-sm font-bold text-center pt-1">တာဝန်ခံဥပဒေအရာရှိ</p>

              <div className="flex items-center">
                <input
                  type="text"
                  name="officerOffice"
                  placeholder="ဥပဒေရုံးအမည်"
                  value={formData.officerOffice || ''}
                  onChange={handleChange}
                  disabled={!isEditable}
                  className="flex-1 bg-transparent border-b border-dotted border-black focus:outline-none text-center text-sm disabled:bg-transparent"
                />
                <span className="text-sm ml-1">ဥပဒေရုံး</span>
              </div>

              <div className="flex items-center gap-4">
                <span className="w-32 shrink-0 whitespace-nowrap text-right text-xs mr-2">တင်ပြသည့်ရက်စွဲ</span>
                <div className="min-w-0 flex-1">
                  <FormDatePicker
                    name="officerSubmissionDate"
                    value={formData.officerSubmissionDate || ""}
                    isEditable={isEditable}
                    onChange={handleChange}
                    onClear={handleClearDate}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons Component Integration */}
        {/* <FormActionButtons
          isPreview={isPreview}
          isEditable={isEditable}
          isSubmitting={isSubmitting}
          isFormSigned={isFormSigned}
          isApproved={isApproved}
          onCancel={handleBackNavigation}
          onSaveDraft={handleSaveDraft}
          onApproveAndSave={handleApproveAndSave}
          onSubmitToSuperior={handleSubmitToSuperior}
        /> */}

        {/* Signature Modal */}
        {isSigning && isEditable && (
        <SignatureModal
          isOpen={isSigning}
          sigCanvasRef={sigCanvasRef}
          onClose={() => {
            setIsSigning(false);
            setActiveSigningRole(null);
          }}
          onClear={() => sigCanvasRef.current?.clear()}
          onSave={handleModalSave}
        />
        )}
      </div>
    </div>
  );
};